import React, { useContext, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Divider,
  Input,
  message,
  Result,
  Row,
  Select,
  Switch,
  Tabs,
} from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Form } from '@ant-design/compatible';
import {
  getUserByIdAction,
  getUserGroupsAction,
  saveUserAction,
} from '../../actions/UserActions';
import { history } from '../../utils/history';
import CustomAddNewPageContent from '../../components/Content/CustomAddNewPageContent';
import Authorized from '../../components/Authorization/Authorized';
import UserContext from '../layout/UserContext';
import checkPermission from '../../components/Authorization/Util';
import AuditTrail from '../../components/AuditTrail/AuditTrail';
import checkEnabledFeatures from '../../components/Authorization/Feature';

import '@ant-design/compatible/assets/index.css';

const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;

const UpdateUser = ({ match, form }) => {
  const dispatch = useDispatch();

  const [finished, setFinished] = useState(false);
  const [userId, setUserId] = useState(0);
  const [confirmDirty, setConfirmDirty] = useState(false);

  const { user, userGroups, currentUser } = useSelector(store => ({
    user: store.userReducer.user,
    userGroups: store.userReducer.userGroups,
    currentUser: store.userReducer.currentUser,
  }));

  useEffect(() => {
    dispatch(getUserGroupsAction());
    const { id } = match.params;
    setUserId(id);
    
    // eslint-disable-next-line react/destructuring-assignment
    dispatch(getUserByIdAction(id));
  }, []);

  const handleConfirmBlur = e => {
    const { value } = e.target;
    setConfirmDirty(confirmDirty || !!value);
  };

  const compareToFirstPassword = (rule, value, callback) => {
    if (value && value !== form.getFieldValue('password')) {
      callback('Two passwords that you enter is inconsistent!');
    } else {
      callback();
    }
  };

  const validateToNextPassword = (rule, value, callback) => {
    if (value && confirmDirty) {
      form.validateFields(['confirm'], { force: true });
    }
    callback();
  };

  const handleSubmit = e => {
    e.preventDefault();

    form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        const { id } = match.params;
        
        const dataToSend = {
          ...values,
          notification: {
            item: {
              create: values.itemCreate ? values.itemCreate : false,
              update: values.itemUpdate ? values.itemUpdate : false,
              delete: values.itemDelete ? values.itemDelete : false,
            },
          },
        };
        delete dataToSend.itemCreate;
        delete dataToSend.itemUpdate;
        delete dataToSend.itemDelete;
        // eslint-disable-next-line react/destructuring-assignment
        dispatch(saveUserAction(dataToSend, id)).then(() => {
          message.success('User is saved!');
          form.resetFields();
          setFinished(true);
        });
      }
    });
  };

  const handleGoUsers = () => {
    history.push('/userList');
  };

  const handleGoAddNew = () => {
    history.push('/addUser');
  };

  const formItemLayout = {
    labelCol: {
      xs: { span: 24 },
      sm: { span: 8 },
    },
    wrapperCol: {
      xs: { span: 24 },
      sm: { span: 16 },
    },
  };

  const { getFieldDecorator } = form;

  // const value = this.context;
  const value = useContext(UserContext);
  const disable = !checkPermission(value, 'user', 'write');

  const itemNotifications = checkEnabledFeatures(
    currentUser,
    'Item-Cataloging',
  ) ? (
    <TabPane tab="Notification Preferences" key="3">
      <Card title="Item Notifications" bordered={false}>
        <Row style={{ marginTop: 30 }}>
          <Col span={8}>
            <Form.Item label="Create">
              {getFieldDecorator('itemCreate', {
                valuePropName: 'checked',
                initialValue:
                  user && user.notification
                    ? user.notification.item.create
                    : undefined,
              })(
                <Switch
                  checkedChildren={<CheckOutlined />}
                  unCheckedChildren={<CloseOutlined />}
                  disabled={disable}
                />,
              )}
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Update">
              {getFieldDecorator('itemUpdate', {
                valuePropName: 'checked',
                initialValue:
                  user && user.notification
                    ? user.notification.item.update
                    : undefined,
              })(
                <Switch
                  checkedChildren={<CheckOutlined />}
                  unCheckedChildren={<CloseOutlined />}
                  disabled={disable}
                />,
              )}
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Delete">
              {getFieldDecorator('itemDelete', {
                valuePropName: 'checked',
                initialValue:
                  user && user.notification
                    ? user.notification.item.delete
                    : undefined,
              })(
                <Switch
                  checkedChildren={<CheckOutlined />}
                  unCheckedChildren={<CloseOutlined />}
                  disabled={disable}
                />,
              )}
            </Form.Item>
          </Col>
        </Row>
      </Card>
    </TabPane>
  ) : (
    ''
  );

  return (
    <CustomAddNewPageContent title="Update User" subTitle="User Management">
      {!finished && (
        <Card bordered={false}>
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          <Form {...formItemLayout} onSubmit={handleSubmit}>
            <Tabs defaultActiveKey="1">
              <TabPane tab="Personal  Details" key="1">
                <Col span={12} offset={4} style={{ marginTop: 30 }}>
                  <Form.Item label="Name">
                    {getFieldDecorator('name', {
                      rules: [
                        {
                          required: true,
                          message: 'Please input your name!',
                        },
                      ],
                      initialValue: user ? user.name : undefined,
                    })(<Input disabled={disable} />)}
                  </Form.Item>
                  <Form.Item label="E-mail">
                    {getFieldDecorator('email', {
                      rules: [
                        {
                          type: 'email',
                          message: 'The input is not valid E-mail!',
                        },
                        {
                          required: true,
                          message: 'Please input your E-mail!',
                        },
                      ],
                      initialValue: user ? user.email : undefined,
                    })(<Input disabled={disable} />)}
                  </Form.Item>
                  <Form.Item label="Username">
                    {getFieldDecorator('username', {
                      rules: [
                        {
                          required: true,
                          message: 'Please input your username!',
                        },
                      ],
                      initialValue: user ? user.username : undefined,
                    })(<Input disabled={disable} />)}
                  </Form.Item>
                  <Form.Item label="User Group">
                    {getFieldDecorator('userGroup', {
                      rules: [
                        {
                          required: true,
                          message: 'Please select user group!',
                        },
                      ],
                      initialValue: user ? user.userGroup.id : undefined,
                    })(
                      <Select
                        allowClear
                        showSearch
                        filterOption
                        optionFilterProp="children"
                        disabled={disable}
                      >
                        {userGroups.map(item => (
                          <Option key={item.id} value={item.id}>
                            {item.value}
                          </Option>
                        ))}
                      </Select>,
                    )}
                  </Form.Item>
                  <Form.Item label="Phone Number">
                    {getFieldDecorator('phoneNumber', {
                      rules: [],
                      initialValue: user ? user.phoneNumber : undefined,
                    })(<Input disabled={disable} />)}
                  </Form.Item>
                  <Form.Item label="Address">
                    {getFieldDecorator('address', {
                      rules: [],
                      initialValue: user ? user.address : undefined,
                    })(<TextArea rows={3} disabled={disable} />)}
                  </Form.Item>
                </Col>
              </TabPane>
              <TabPane tab="Security Details" key="2">
                <Col span={12} offset={4} style={{ marginTop: 30 }}>
                  <Form.Item label="Password" hasFeedback>
                    {getFieldDecorator('password', {
                      rules: [
                        {
                          validator: validateToNextPassword,
                        },
                      ],
                    })(<Input.Password disabled={disable} />)}
                  </Form.Item>
                  <Form.Item label="Confirm Password" hasFeedback>
                    {getFieldDecorator('confirm', {
                      rules: [
                        {
                          validator: compareToFirstPassword,
                        },
                      ],
                    })(
                      <Input.Password
                        onBlur={handleConfirmBlur}
                        disabled={disable}
                      />,
                    )}
                  </Form.Item>
                </Col>
              </TabPane>
              {itemNotifications}
            </Tabs>
          </Form>

          <Authorized asset="user" operation="write">
            <Divider />
            <Row type="flex" justify="center">
              <Button type="primary" onClick={handleSubmit}>
                <CheckOutlined /> Save
              </Button>
            </Row>
            <Divider />
            <AuditTrail objectId={userId} objectType="user" />
          </Authorized>
        </Card>
      )}
      {finished && (
        <Result
          status="success"
          title="Successfully inserted new item!"
          // subTitle="Order number: 2017182818828182881 Cloud server configuration takes 1-5 minutes, please wait."
          extra={[
            <Button type="primary" key="console" onClick={handleGoUsers}>
              Go to users
            </Button>,
            <Button key="buy" onClick={handleGoAddNew}>
              Add user
            </Button>,
          ]}
        />
      )}
    </CustomAddNewPageContent>
  );
};
UpdateUser.propTypes = {
  form: PropTypes.object,
  userGroups: PropTypes.array,
  getUserGroupsAction: PropTypes.func,
  currentUser: PropTypes.object,
  saveUserAction: PropTypes.func,
  getUserByIdAction: PropTypes.func,
  user: PropTypes.object,
  match: PropTypes.object,
};

UpdateUser.contextType = UserContext;

const WrappedUpdateUser = Form.create({ name: 'updateUser' })(UpdateUser);

function mapStateToProps({ userReducer }) {
  const { userGroups, user } = userReducer;
  return { userGroups, user };
}
export default connect(mapStateToProps, {
  getUserGroupsAction,
  getUserByIdAction,
  saveUserAction,
})(WrappedUpdateUser);
